(() => {
  'use strict';

  const FIELD_CLASS = 'indigo-checkout-phone';
  const ATTRIBUTE_NAME = 'Contact phone';
  const ATTRIBUTE_INPUT_NAME = `attributes[${ATTRIBUTE_NAME}]`;
  const CART_ACTIONS_SELECTOR = '.cart__ctas';
  const PHONE_PATTERN = /^\+?[0-9][0-9\s().-]{8,18}$/;
  const SAVE_DELAY = 500;
  let currentPhone = '';
  let hydratePromise;

  const normalize = (value) => String(value || '').trim();
  const digitCount = (value) => normalize(value).replace(/\D/g, '').length;
  const validPhone = (value) => PHONE_PATTERN.test(normalize(value)) && digitCount(value) >= 10 && digitCount(value) <= 15;

  const loadCartPhone = async () => {
    if (!hydratePromise) {
      hydratePromise = fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then((response) => {
        if (!response.ok) throw new Error('Unable to read cart');
        return response.json();
      }).then((cart) => normalize(cart?.attributes?.[ATTRIBUTE_NAME])).catch(() => '');
    }
    currentPhone = await hydratePromise;
    return currentPhone;
  };

  const saveCartPhone = async (phone) => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [ATTRIBUTE_NAME]: phone } })
    });
    if (!response.ok) throw new Error('Unable to save phone number');
    currentPhone = phone;
  };

  const syncNativeInputs = (phone) => {
    document.querySelectorAll('.cart-form').forEach((form) => {
      let hidden = form.querySelector('input[data-indigo-phone-attribute]');
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = ATTRIBUTE_INPUT_NAME;
        hidden.dataset.indigoPhoneAttribute = 'true';
        form.appendChild(hidden);
      }
      hidden.value = phone;
    });
  };

  const syncVisibleInputs = (phone, source) => {
    document.querySelectorAll(`.${FIELD_CLASS} input`).forEach((input) => {
      if (input !== source) input.value = phone;
    });
    syncNativeInputs(phone);
  };

  const fieldMarkup = () => {
    const wrapper = document.createElement('div');
    const id = `indigo-cart-phone-${Math.random().toString(36).slice(2)}`;
    let saveTimer;
    let lastSaved = null;
    wrapper.className = FIELD_CLASS;
    wrapper.innerHTML = `
      <label class="indigo-checkout-phone__label" for="${id}">Mobile number for order coordination <span class="visually-hidden">(optional)</span></label>
      <input id="${id}" class="indigo-checkout-phone__input" type="tel" inputmode="tel" autocomplete="tel" placeholder="e.g. +92 300 1234567" aria-describedby="${id}-help ${id}-status">
      <p class="indigo-checkout-phone__help" id="${id}-help">Optional. Add a number so Indigo Studio can coordinate delivery or confirm a Cash on Delivery order when necessary.</p>
      <p class="indigo-checkout-phone__error" id="${id}-status" role="status" aria-live="polite" hidden></p>`;

    const input = wrapper.querySelector('input');
    const status = wrapper.querySelector('.indigo-checkout-phone__error');

    const setStatus = (message, isError = false) => {
      if (!status) return;
      status.textContent = message;
      status.hidden = !message;
      input.toggleAttribute('aria-invalid', isError);
    };

    const persist = async () => {
      const phone = normalize(input.value);
      syncNativeInputs(phone);
      if (phone && !validPhone(phone)) {
        setStatus('Please check the number. Use 10 to 15 digits, including the country code when possible.', true);
        return;
      }
      if (phone === lastSaved || phone === currentPhone) return;
      try {
        await saveCartPhone(phone);
        lastSaved = phone;
        setStatus(phone ? 'Mobile number saved with your cart.' : '');
      } catch (_) {
        setStatus('The number could not be saved right now. Checkout is still available.', true);
      }
    };

    const schedulePersist = () => {
      clearTimeout(saveTimer);
      saveTimer = setTimeout(persist, SAVE_DELAY);
    };

    input.addEventListener('input', () => {
      const value = normalize(input.value);
      syncVisibleInputs(value, input);
      setStatus('');
      schedulePersist();
    });
    input.addEventListener('change', persist);
    input.addEventListener('blur', persist);

    loadCartPhone().then((phone) => {
      if (!normalize(input.value)) input.value = phone;
      syncVisibleInputs(normalize(input.value), input);
      lastSaved = phone;
    });
    return wrapper;
  };

  const installFields = (root = document) => {
    root.querySelectorAll?.(CART_ACTIONS_SELECTOR).forEach((actions) => {
      const container = actions.parentElement || actions;
      if (!container.querySelector(`:scope > .${FIELD_CLASS}`)) actions.insertAdjacentElement('beforebegin', fieldMarkup());
    });
    root.querySelectorAll?.('.indigo-cod-phone').forEach((legacy) => legacy.remove());
    root.querySelectorAll?.('.additional-checkout-buttons, shopify-accelerated-checkout-cart').forEach((element) => {
      element.hidden = false;
      element.removeAttribute('aria-hidden');
    });
    syncNativeInputs(currentPhone);
  };

  const saveBeforeCheckout = (event) => {
    const action = event.target instanceof Element ? event.target.closest('.cart__ctas button, .cart__ctas a, .cart__ctas shopify-accelerated-checkout-cart') : null;
    if (!action) return;
    const input = action.closest('.cart-summary, .cart-drawer__summary')?.parentElement?.querySelector(`.${FIELD_CLASS} input`) || document.querySelector(`.${FIELD_CLASS} input`);
    if (!input) return;
    const phone = normalize(input.value);
    syncNativeInputs(phone);
    if (!phone || validPhone(phone)) saveCartPhone(phone).catch(() => {});
  };

  let scheduled = false;
  const scheduleInstall = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      installFields();
    });
  };

  const init = () => {
    installFields();
    document.addEventListener('pointerdown', saveBeforeCheckout, true);
    document.addEventListener('submit', saveBeforeCheckout, true);
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => [...mutation.addedNodes].some((node) => node.nodeType === 1))) scheduleInstall();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init, { once: true }) : init();
})();
